import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { API_BASE_URL } from '../config'

/**
 * Axios instance for the BOL2 external API.
 *
 * Adapted from invoice-assistant-ui/src/lib/axiosConfig.ts. The differences that
 * matter here:
 *   - `withCredentials` stays false. The task pane is a third-party iframe inside
 *     Outlook, so BOL2's cookies are never sent; the bearer token is the only auth.
 *   - The 401 refresher is the Office sign-in dialog rather than a cookie call,
 *     wired up by OfficeAuthProvider via setAuthTokenRefresher.
 */

let currentAuthToken: string | undefined
let refreshAuthToken: (() => Promise<string | undefined>) | undefined
let refreshPromise: Promise<string | undefined> | null = null

export function setAuthToken(token?: string): void {
    currentAuthToken = token
}

export function setAuthTokenRefresher(refresher?: () => Promise<string | undefined>): void {
    refreshAuthToken = refresher
}

const instance = axios.create({
    baseURL: API_BASE_URL,
    responseType: 'json',
    withCredentials: false,
    timeout: 30_000,
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
    },
})

instance.interceptors.request.use(
    (config) => {
        if (currentAuthToken) {
            config.headers.Authorization = `Bearer ${currentAuthToken}`
        } else {
            delete config.headers.Authorization
        }
        return config
    },
    (error) => Promise.reject(error),
)

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean }

instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const status = error.response?.status
        const original = error.config as RetriableConfig | undefined

        // BOL2 answers an expired/absent token with 401 from the JWT middleware and
        // 403 from the route authorisation middleware, so retry once on either.
        const isAuthFailure = status === 401 || status === 403

        if (!isAuthFailure || !refreshAuthToken || !original || original._retry) {
            return Promise.reject(error)
        }

        original._retry = true

        try {
            // Share one in-flight dialog across concurrent 401s, so a panel with
            // several queries in flight cannot open several sign-in dialogs.
            refreshPromise ??= refreshAuthToken()
            const token = await refreshPromise

            if (!token) {
                return Promise.reject(error)
            }

            setAuthToken(token)
            original.headers.Authorization = `Bearer ${token}`
            return instance(original)
        } catch (refreshError) {
            return Promise.reject(refreshError)
        } finally {
            refreshPromise = null
        }
    },
)

export const api = instance

/** Shape every BOL2 external endpoint wraps its payload in. */
export type BolResponse<T> = {
    success: boolean
    message: string
    data: T
}

/** Extracts a human-readable message from a failed BOL2 call. */
export function apiErrorMessage(error: unknown, fallback = 'Something went wrong'): string {
    if (axios.isAxiosError(error)) {
        const message = (error.response?.data as { message?: unknown } | undefined)?.message
        if (typeof message === 'string' && message.trim() !== '') {
            return message
        }
        if (error.code === 'ECONNABORTED') {
            return 'Blocks Online took too long to respond'
        }
        if (!error.response) {
            return 'Could not reach Blocks Online'
        }
    }
    if (error instanceof Error && error.message) {
        return error.message
    }
    return fallback
}
